import type { Distribution, PolicyDetails, PolicyP, PolicyPreset } from '@/api-client/api';
import type { TAtPath } from '@/api-client/exctract-types';
import constants from '@/constants';
import { type ActionState } from '@/models/Action';
import { useMainStore } from '@/stores/main';

export const CLUSTER_SCOPED_LABEL = 'cluster-scoped resources';

export const POLICY_VALIDATION_STATUSES = ['Valid', 'Invalid', 'Unknown'] as const;
export type PolicyValidation = (typeof POLICY_VALIDATION_STATUSES)[number];

export const POLICY_FREQUENCY_VALUES = [
  'Hourly',
  'Daily',
  'Weekly',
  'Monthly',
  'Yearly',
  'On Demand',
  'Paused',
] as const;
export type PolicyFrequencyValue = (typeof POLICY_FREQUENCY_VALUES)[number];
export type PolicyFrequency =
  | {
      source: 'Policy';
      value?: PolicyFrequencyValue;
    }
  | {
      source: 'Preset';
      preset: {
        name: string;
        namespace: string;
      };
      value?: PolicyFrequencyValue;
      unauthorized?: boolean;
    };

export const GLOBAL_POLICY_FREQUENCY_VALUES = [
  'Hourly',
  'Daily',
  'Weekly',
  'Monthly',
  'Yearly',
  'On Demand',
] as const;
export type GlobalPolicyFrequency = Exclude<PolicyFrequencyValue, 'Paused'>;

export const POLICY_ACTIONS = [
  'Import',
  'Import + Restore',
  'Snapshot',
  'Snapshot + Export',
  'Report',
] as const;
export type PolicyAction = (typeof POLICY_ACTIONS)[number];

export const GLOBAL_POLICY_ACTIONS = [
  'Import',
  'Import + Restore',
  'Snapshot',
  'Snapshot + Export',
] as const;
export type GlobalPolicyAction = Exclude<PolicyAction, 'Report'>;
export type PolicyViewExportFrequencyItem = {
  profileName: string;
  frequency: string;
};

export type PolicyViewExportRetentionItem = {
  profileName: string;
  retention: Partial<TAtPath<PolicyP, ['spec', 'retention']>>[];
};

export type PolicyViewRetentionScheduleType = {
  snapshot?: Partial<TAtPath<PolicyP, ['spec', 'retention']>>[];
  export?: PolicyViewExportRetentionItem[];
};

export type PolicyActionObj = TAtPath<PolicyP, ['spec', 'actions', number]>;
export type PolicySnapshotHook = TAtPath<PolicyActionObj, ['backupParameters', 'hooks']>;
export type PolicyExportHook = TAtPath<PolicyActionObj, ['exportParameters', 'hooks']>;
export type PolicyVolumeSnapshots = TAtPath<
  PolicyActionObj,
  ['exportParameters', 'volumeSnapshots']
>;
// `PolicyExportData` narrows the swagger-generated API types to only the
// top-level fields the policy-view consumers (`PolicyViewExportDurability`,
// `PolicyViewAdvancedExport`) actually read. If a future swagger regen
// drops or renames any of these picked keys, this `Pick<...>` fails to
// typecheck — the failure surfaces here, not silently in the consumers.
// Inner shapes (e.g. `overrides[].enabled`, `awsEbs.regions`) still flow
// through from the API; if you need protection on those too, narrow them
// recursively here.
type ApiExportConfig = NonNullable<TAtPath<PolicyActionObj, ['exportParameters', 'exportData']>>;
type ApiVolumeSnapshots = NonNullable<PolicyVolumeSnapshots>;

export type PolicyExportData =
  | {
      exportData: Pick<ApiExportConfig, 'enabled' | 'exporterStorageClassName' | 'overrides'>;
      volumeSnapshot: Pick<ApiVolumeSnapshots, 'awsEbs' | 'azure'> | undefined;
    }
  | undefined;
export type PolicyViewHooks = {
  action: 'snapshot' | 'export' | 'restore';
  hooks: PolicySnapshotHook | PolicyExportHook;
};

export type PolicyLocationProfile =
  | {
      action?: 'import' | 'export' | 'blueprint' | 'container-images' | 'block-mode';
      name?: string;
      namespace?: string;
    }
  | undefined;

export type MaybeMissingPolicyLocationProfile = PolicyLocationProfile & {
  isMissing: boolean;
};

export type PolicyResourceType = 'Label' | 'Namespace' | 'ExcludedNamespace' | 'ClusterScoped';
export type PolicyResource = {
  type: PolicyResourceType;
  name: string;
};

export type ImportData = {
  profileName: string;
  importDataToken: string;
};

export type PolicyTableSummary = {
  name: string;
  validation: PolicyValidation;
  resources?: PolicyResource[];
  frequency: PolicyFrequency;
  action: PolicyAction | 'Unknown';
  locationProfiles?: PolicyLocationProfile[];
  lastRun?: {
    status?: ActionState;
    time?: string;
    unauthorized?: boolean;
  };
};

export type VmLabelSelectorSummary = {
  labels: Array<{ key: string; value: string }>;
  // Empty `namespaces` means the policy targets VMs across all namespaces
  // (the wildcard `*` we send to satisfy the BE's "at least one namespace
  // value" requirement when the user opts out of specifying namespaces).
  namespaces: string[];
};

export type PolicyViewSummary = {
  snapshotHooks: PolicyViewHooks[];
  exportHooks: PolicyViewHooks[];
  restoreHooks: PolicyViewHooks[];
  includeResources?: Array<{
    group?: string;
    matchExpressions?: Array<{
      key: string;
      operator: string;
      values?: Array<string>;
    }>;
    matchLabels?: Record<string, string>;
    name?: string;
    resource?: string;
    version?: string;
  }>;
  excludeResources?: Array<{
    group?: string;
    matchExpressions?: Array<{
      key: string;
      operator: string;
      values?: Array<string>;
    }>;
    matchLabels?: Record<string, string>;
    name?: string;
    resource?: string;
    version?: string;
  }>;
  snapshotFrequency?: {
    frequency?: PolicyFrequencyValue;
    subFrequency?: TAtPath<PolicyP, ['spec', 'subFrequency']>;
    backupWindow?: TAtPath<PolicyP, ['spec', 'backupWindow']>;
  };
  retentionSchedule?: PolicyViewRetentionScheduleType;
  locationProfiles?: PolicyLocationProfile[];
  exportFrequency?: PolicyViewExportFrequencyItem[];
  exportData?: PolicyExportData;
  importData?: ImportData[];
  nsToVmsMap?: NamespaceProtectedVmSummary[];
  vmLabelSelector?: VmLabelSelectorSummary;
};

export type NamespaceProtectedVmSummary = {
  namespace: string;
  protectedVms: string | 'all';
};

export type GlobalPolicyTableSummary = Omit<PolicyTableSummary, 'frequency'> & {
  frequency?: GlobalPolicyFrequency;
  distributions?: Distribution[];
};

export type PolicyWithTableSummary = {
  policy: PolicyP;
  tableSummary: PolicyTableSummary;
};

export type PolicyWithViewSummary = {
  policy: PolicyP;
  viewSummary: Omit<PolicyTableSummary, 'lastRun' | 'frequency'> & PolicyViewSummary;
};

export type GlobalPolicyWithTableSummary = {
  policy: PolicyP;
  tableSummary: GlobalPolicyTableSummary;
};

export type GlobalPolicyWithViewSummary = {
  policy: PolicyP;
  viewSummary: Omit<GlobalPolicyTableSummary, 'lastRun' | 'frequency'> & PolicyViewSummary;
};

export type MatchExpressions = {
  key: string;
  operator: string;
  values?: string[];
};

export type MatchLabels = Record<string, string>;

const UIValidationMap: Record<string, PolicyValidation> = {
  success: 'Valid',
  failed: 'Invalid',
};

export const mapToUIValidation = (policy: PolicyP) => {
  const v = policy.status?.validation?.toLowerCase();
  return v ? (UIValidationMap[v] ?? 'Unknown') : 'Unknown';
};

const countActions = (actions: TAtPath<PolicyP, ['spec', 'actions']>) =>
  actions.reduce<Record<string, number>>((count, item) => {
    if (item.action) {
      const a = item.action.toLowerCase();
      count[a] = (count[a] || 0) + 1;
    }
    return count;
  }, {});

type ActionStrategy = {
  matches: (countedActions: Record<string, number>) => boolean;
  value: PolicyAction;
};

const actionStrategies: ActionStrategy[] = [
  {
    matches: (countedActions) => countedActions.import > 0 && countedActions.restore > 0,
    value: 'Import + Restore',
  },

  {
    matches: (countedActions) => countedActions.import > 0 && !countedActions.restore,
    value: 'Import',
  },

  {
    matches: (countedActions) => countedActions.backup > 0 && countedActions.export > 0,
    value: 'Snapshot + Export',
  },

  {
    matches: (countedActions) => countedActions.backup > 0 && !countedActions.export,
    value: 'Snapshot',
  },
  {
    matches: (countedActions) => countedActions.report > 0,
    value: 'Report',
  },
];

export const mapToUIAction = (policy: PolicyP) => {
  const actions = policy.spec?.actions ?? [];
  const countedActions = countActions(actions);

  const strategy = actionStrategies.find((s) => s.matches(countedActions));
  return strategy?.value ?? 'Unknown';
};

export const UIFrequencyValueMap: Record<string, GlobalPolicyFrequency> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  ondemand: 'On Demand',
};

export const normalizeFrequency = (frequency?: string) =>
  frequency?.replaceAll('@', '').toLowerCase().trim();

export const mapToUIPolicyFrequency = (
  policy: PolicyP,
  preset?: PolicyDetails['preset'],
): PolicyFrequency => {
  const paused = policy.spec?.paused;
  const policyHasPreset = policy.spec?.presetRef;

  const getFrequency = () => {
    if (!policyHasPreset) return UIFrequencyValueMap[normalizeFrequency(policy.spec?.frequency)!];
    if (policyHasPreset && preset?.unauthorized) return undefined;
    if (
      policyHasPreset &&
      preset?.preset &&
      preset.preset.metadata?.name === policy.spec?.presetRef?.name &&
      preset.preset.metadata?.namespace === policy.spec?.presetRef?.namespace
    ) {
      return UIFrequencyValueMap[normalizeFrequency(preset.preset?.spec.backup?.frequency)!];
    }
  };

  if (policyHasPreset) {
    return {
      source: 'Preset',
      preset: {
        name: policy.spec?.presetRef?.name as string,
        namespace: policy.spec?.presetRef?.namespace as string,
      },
      value: paused ? 'Paused' : getFrequency(),
      unauthorized: preset?.unauthorized,
    };
  } else {
    return {
      source: 'Policy',
      value: paused ? 'Paused' : getFrequency(),
    };
  }
};

export const mapToUIGlobalPolicyFrequency = (policy: PolicyP) =>
  UIFrequencyValueMap[normalizeFrequency(policy.spec?.frequency)!];

export const mapToUIHooks = (
  policy: PolicyP,
): {
  action: 'snapshot' | 'export' | 'restore';
  hooks: PolicySnapshotHook | PolicyExportHook;
}[] => {
  const actions = policy?.spec?.actions ?? [];
  return actions
    .map((action) => {
      if (action.action === 'backup' && action.backupParameters?.hooks) {
        return {
          // backup === snapshot
          action: 'snapshot' as const,
          hooks: action.backupParameters.hooks,
        };
      }
      if (action.action === 'export' && action.exportParameters?.hooks) {
        return {
          action: 'export' as const,
          hooks: action.exportParameters.hooks,
        };
      }
      if (action.action === 'restore' && action.restoreParameters?.hooks) {
        return {
          action: 'restore' as const,
          hooks: action.restoreParameters.hooks,
        };
      }
      return null;
    })
    .filter(
      (item): item is { action: 'snapshot' | 'export' | 'restore'; hooks: any } => item !== null,
    );
};

export const mapToUIIncludeExcludeResources = (policy: PolicyP, type: 'include' | 'exclude') => {
  const actions = policy?.spec?.actions ?? [];
  const resourcesLookup = {
    backup: (a: TAtPath<PolicyP, ['spec', 'actions', number]>) => {
      const resources =
        type === 'include'
          ? [
              ...(a.backupParameters?.filters?.includeResources ?? []),
              ...(a.backupParameters?.filters?.includeClusterResources ?? []),
            ]
          : [
              ...(a.backupParameters?.filters?.excludeResources ?? []),
              ...(a.backupParameters?.filters?.excludeClusterResources ?? []),
            ];
      return resources;
    },
    restore: (a: TAtPath<PolicyP, ['spec', 'actions', number]>) => {
      const resources =
        type === 'include'
          ? [
              ...(a.restoreParameters?.filters?.includeResources ?? []),
              ...(a.restoreParameters?.filters?.includeClusterResources ?? []),
            ]
          : [
              ...(a.restoreParameters?.filters?.excludeResources ?? []),
              ...(a.restoreParameters?.filters?.excludeClusterResources ?? []),
            ];
      return resources;
    },
  };

  return actions
    .map((action) => {
      const resources = resourcesLookup[action.action as keyof typeof resourcesLookup];
      if (resources) {
        return resources(action) ?? [];
      }
      return [];
    })
    .flat();
};

export const mapToUIImportDataTokens = (
  actionType: PolicyAction | 'Unknown',
  policy: PolicyP,
): ImportData[] | undefined => {
  if (actionType !== 'Snapshot + Export') return undefined;

  const importDataTokens = (policy.spec?.actions ?? [])
    .filter((a) => a.action === 'export' && a.exportParameters?.receiveString)
    .map((a) => ({
      profileName: a.exportParameters?.profile?.name ?? '',
      importDataToken: a.exportParameters?.receiveString ?? '',
    }));

  return importDataTokens.length > 0 ? importDataTokens : undefined;
};

export type PolicyPResourcesObj = {
  matchExpressions?: MatchExpressions[];
  matchLabels?: MatchLabels;
};

export const mapToUIResources = (policyResourcesObj?: PolicyPResourcesObj) => {
  const resources: PolicyResource[] = [];

  if (!policyResourcesObj) return undefined;

  const { matchExpressions, matchLabels } = policyResourcesObj;

  if (!matchExpressions && !matchLabels) return undefined;

  if (matchLabels) {
    Object.entries(matchLabels).forEach(([key, value]) => {
      resources.push({ type: 'Label', name: `${key}:${value}` });
    });
  }

  if (matchExpressions) {
    matchExpressions.forEach(({ key, operator, values }) => {
      const isExcluded = operator === 'NotIn';
      values?.forEach((value) => {
        if (value === constants.NS_CLUSTER_RESOURCES) {
          resources.push({ type: 'ClusterScoped', name: CLUSTER_SCOPED_LABEL });
        } else if (key === constants.KASTEN_APP_NS) {
          resources.push({
            type: isExcluded ? 'ExcludedNamespace' : 'Namespace',
            name: isExcluded ? `!${value}` : value,
          });
        } else {
          resources.push({ type: 'Label', name: `${key}:${value}` });
        }
      });
    });
  }

  return resources;
};

export const mapToUILocationProfiles = (policy: PolicyP): PolicyLocationProfile[] | undefined => {
  const actions = policy?.spec?.actions ?? [];

  return actions.reduce(
    (
      locationProfiles: PolicyLocationProfile[],
      action: TAtPath<PolicyP, ['spec', 'actions', number]>,
    ) => {
      if (action.action === 'export' && action.exportParameters?.profile) {
        locationProfiles.push({
          action: 'export',
          name: action.exportParameters.profile.name,
          namespace: action.exportParameters.profile.namespace,
        });
      }
      if (action.action === 'export' && action.exportParameters?.blockModeProfile) {
        locationProfiles.push({
          action: 'block-mode',
          name: action.exportParameters.blockModeProfile.name,
          namespace: action.exportParameters.blockModeProfile.namespace,
        });
      }
      if (action.action === 'import' && action.importParameters?.profile) {
        locationProfiles.push({
          action: 'import',
          name: action.importParameters.profile.name,
          namespace: action.importParameters.profile.namespace,
        });
      }
      if (action.action === 'backup' && action.backupParameters?.profile) {
        locationProfiles.push({
          action: 'blueprint',
          name: action.backupParameters.profile.name,
          namespace: action.backupParameters.profile.namespace,
        });
      }
      if (action.action === 'backup' && action.backupParameters?.imageRepoProfile) {
        locationProfiles.push({
          action: 'container-images',
          name: action.backupParameters.imageRepoProfile.name,
          namespace: action.backupParameters.imageRepoProfile.namespace,
        });
      }
      return locationProfiles;
    },
    [],
  );
};

const mapToUIRetentionSchedule = (policy: PolicyP) => {
  const spec = policy?.spec;
  const exportActions = (spec?.actions ?? []).filter(
    (action) => action.action === 'export' && action.retention,
  );

  const snapshotKeys = spec?.retention ? Object.keys(spec.retention) : [];

  // If neither snapshot nor export retention values exist, return undefined
  if (snapshotKeys.length === 0 && exportActions.length === 0) {
    return undefined;
  }

  const exportItems: PolicyViewExportRetentionItem[] = exportActions.map((exportAction) => {
    const exportKeys = exportAction.retention ? Object.keys(exportAction.retention) : [];
    return {
      profileName: exportAction.exportParameters?.profile?.name ?? '',
      retention: exportKeys.map((key) => ({ [key]: exportAction.retention?.[key] })),
    };
  });

  return {
    snapshot: snapshotKeys.map((key) => ({ [key]: spec?.retention?.[key] })),
    export: exportItems.length > 0 ? exportItems : undefined,
  };
};

const mapToUISnapshotFrequency = (policy: PolicyP) => {
  const spec = policy?.spec;
  const paused = spec?.paused;

  return {
    frequency: (paused
      ? 'Paused'
      : UIFrequencyValueMap[normalizeFrequency(policy.spec?.frequency)!]) as PolicyFrequencyValue,
    subFrequency: spec?.subFrequency,
    backupWindow: spec?.backupWindow,
  };
};

const mapToUIExportFrequency = (policy: PolicyP): PolicyViewExportFrequencyItem[] | undefined => {
  const exportActions = (policy?.spec?.actions ?? []).filter(
    (a) => a.action === 'export' && a.exportParameters?.frequency,
  );
  if (!exportActions.length) return undefined;
  return exportActions.map((a) => ({
    profileName: a.exportParameters?.profile?.name ?? '',
    frequency: a.exportParameters?.frequency?.replaceAll('@', '').toLowerCase().trim() ?? '',
  }));
};

export const getDistributionsForPolicy = (policy: PolicyP): Distribution[] => {
  const store = useMainStore();

  // The distributions are fetched on the Multicluster root page in the onBeforeMount lifecycle hook
  return store.distributions.reduce((distributions: Distribution[], dist: Distribution) => {
    if (dist.spec?.sources?.find((source) => source.ref?.name === policy.metadata?.name)) {
      if (dist.metadata?.name) {
        distributions.push(dist);
      }
    }
    return distributions;
  }, []);
};

const mapToUIExportData = (policy: PolicyP): PolicyExportData => {
  const action = policy.spec?.actions?.find(
    (a) => a.action === 'export' && a.exportParameters?.exportData,
  );
  if (!action?.exportParameters?.exportData) return undefined;
  return {
    exportData: action.exportParameters.exportData,
    volumeSnapshot: action.exportParameters.volumeSnapshots,
  };
};

export const resolveExportDataFromPreset = (
  policy: PolicyP | undefined,
  preset: PolicyPreset | undefined,
  systemNamespace: string,
): PolicyExportData => {
  if (!policy) return undefined;

  const presetRef = policy.spec?.presetRef;
  if (!presetRef) return mapToUIExportData(policy);

  const matchesRef =
    preset?.metadata?.name === presetRef.name &&
    preset?.metadata?.namespace === (presetRef.namespace ?? systemNamespace);
  const presetExport = matchesRef ? preset?.spec?.export : undefined;
  if (!presetExport?.exportData) return undefined;

  return {
    exportData: presetExport.exportData,
    volumeSnapshot: presetExport.volumeSnapshots,
  };
};

export const mapToUIVmLabelSelector = (policy: PolicyP): VmLabelSelectorSummary | undefined => {
  const selector = policy.spec?.selector;
  const matchExpressions = selector?.matchExpressions;
  const matchLabels = selector?.matchLabels;

  // VM-by-label: a single KASTEN_VM_NAMESPACE In expression paired with a
  // non-empty matchLabels block. Mirror the detection used in PolicyForm.
  const isVmByLabel =
    matchExpressions?.[0]?.key === constants.KASTEN_VM_NAMESPACE &&
    !!matchLabels &&
    Object.keys(matchLabels).length > 0;

  if (!isVmByLabel) return undefined;

  const namespaceValues = matchExpressions?.[0]?.values ?? [];
  const isWildcardOnly =
    namespaceValues.length === 1 && namespaceValues[0] === constants.NS_WILDCARD;

  return {
    labels: Object.entries(matchLabels ?? {}).map(([key, value]) => ({ key, value })),
    namespaces: isWildcardOnly ? [] : [...namespaceValues],
  };
};

export const mapToUINSToVMs = (policy: PolicyP): NamespaceProtectedVmSummary[] | undefined => {
  const vmValues = policy.spec?.selector?.matchExpressions?.find(
    ({ key }) => key === constants.KASTEN_VM_REF,
  )?.values;

  if (!vmValues?.length) return undefined;

  const namespaceCounts = vmValues
    .map((value) => {
      const [namespace, vm] = value?.split('/', 2) ?? [];
      return namespace && vm ? { namespace, vm } : undefined;
    })
    .filter((entry): entry is { namespace: string; vm: string } => Boolean(entry))
    .reduce<Record<string, number | 'all'>>((acc, { namespace, vm }) => {
      if (acc[namespace] === 'all') return acc;
      if (vm === '*') {
        acc[namespace] = 'all';
        return acc;
      }
      acc[namespace] = (typeof acc[namespace] === 'number' ? (acc[namespace] as number) : 0) + 1;
      return acc;
    }, {});

  return Object.keys(namespaceCounts).length
    ? Object.entries(namespaceCounts).map(([namespace, count]) => ({
        namespace,
        protectedVms: count === 'all' ? 'all' : count.toString(),
      }))
    : undefined;
};

export const addDistributionsToTableSummary = (
  policy: PolicyWithTableSummary,
): GlobalPolicyWithTableSummary => ({
  policy: policy.policy,
  tableSummary: {
    ...policy.tableSummary,
    frequency: policy.tableSummary.frequency.value as GlobalPolicyFrequency,
    distributions: getDistributionsForPolicy(policy.policy),
  },
});

export const addDistributionsToViewSummary = (
  policy: PolicyWithViewSummary,
): GlobalPolicyWithViewSummary => ({
  policy: policy.policy,
  viewSummary: { ...policy.viewSummary, distributions: getDistributionsForPolicy(policy.policy) },
});

/**
 * Maps a PolicyDetails object to a PolicyWithTableSummary object. This is used when mapping a list of PolicyDetails (from a GET /policies request). The lastRun field is included in this endpoint which returns a list of PolicyDetails.
 * @param policyDetails a policy object from a PolicyDetails call
 * @returns a PolicyWithTableSummary object with the information to display the policy table
 */
export const toPolicyWithTableSummary = (policyDetails: PolicyDetails): PolicyWithTableSummary => {
  const policy = policyDetails.policy ?? {};
  const action = mapToUIAction(policy);

  return {
    policy: policyDetails.policy ?? {},
    tableSummary: {
      name: policy.metadata?.name ?? '',
      validation: mapToUIValidation(policy),
      resources: mapToUIResources(policy.spec?.selector as PolicyPResourcesObj),
      frequency: mapToUIPolicyFrequency(policy, policyDetails.preset),
      action,
      locationProfiles: mapToUILocationProfiles(policy),
      lastRun: policyDetails.lastRun
        ? {
            status: policyDetails.lastRun?.state as ActionState,
            time: policyDetails.lastRun?.time,
            unauthorized: policyDetails.lastRun?.unauthorized,
          }
        : undefined,
    },
  };
};

export const toGlobalPolicyWithTableSummary = (
  policyDetails: PolicyDetails,
): GlobalPolicyWithTableSummary => {
  const policy = policyDetails.policy ?? {};
  const action = mapToUIAction(policy);

  return {
    policy,
    tableSummary: {
      name: policy.metadata?.name ?? '',
      validation: mapToUIValidation(policy),
      resources: mapToUIResources(policy.spec?.selector as PolicyPResourcesObj),
      frequency: mapToUIGlobalPolicyFrequency(policy),
      action,
      locationProfiles: mapToUILocationProfiles(policy),
      lastRun: policyDetails.lastRun
        ? {
            status: policyDetails.lastRun?.state as ActionState,
            time: policyDetails.lastRun?.time,
            unauthorized: policyDetails.lastRun?.unauthorized,
          }
        : undefined,
      distributions: getDistributionsForPolicy(policy),
    },
  };
};

/**
 * Maps a PolicyP (from a PolicyCR) object to a PolicyWithViewSummary object. Because this is used when mapping a PolicyCR (from a single GET /policyCR request), the lastRun field is not included.
 * To include the lastRun field, use toPolicyWithTableSummary or we will need an addition backend endpoint that returns the lastRun field.
 * @param policy a policy object from a PolicyCR call
 * @returns a PolicyWithViewSummary object with the information to display the policy view/details page
 */
export const toPolicyWithViewSummary = (policy: PolicyP): PolicyWithViewSummary => {
  const action = mapToUIAction(policy);
  const hasVmMatchExpression = Boolean(
    policy.spec?.selector?.matchExpressions?.some(({ key }) => key === constants.KASTEN_VM_REF),
  );
  const vmLabelSelector = mapToUIVmLabelSelector(policy);
  return {
    policy,
    viewSummary: {
      name: policy.metadata?.name ?? '',
      validation: mapToUIValidation(policy),
      // Suppress the generic "Applications" resources display for VM-by-label
      // policies — the KASTEN_VM_NAMESPACE expression and matchLabels are
      // surfaced through the dedicated VM section instead.
      resources: vmLabelSelector
        ? undefined
        : mapToUIResources(policy.spec?.selector as PolicyPResourcesObj),
      // We need to either have the preset returned by a new endpoint or fetch it from the preset endpoint and pass it in here
      action,
      importData: mapToUIImportDataTokens(action, policy),
      includeResources: mapToUIIncludeExcludeResources(policy, 'include'),
      excludeResources: mapToUIIncludeExcludeResources(policy, 'exclude'),
      snapshotHooks: mapToUIHooks(policy).filter((item) => item.action === 'snapshot'),
      exportHooks: mapToUIHooks(policy).filter((item) => item.action === 'export'),
      restoreHooks: mapToUIHooks(policy).filter((item) => item.action === 'restore'),
      snapshotFrequency: mapToUISnapshotFrequency(policy),
      retentionSchedule: mapToUIRetentionSchedule(policy),
      locationProfiles: mapToUILocationProfiles(policy),
      exportFrequency: mapToUIExportFrequency(policy),
      nsToVmsMap: hasVmMatchExpression ? mapToUINSToVMs(policy) : undefined,
      vmLabelSelector,
    },
  };
};
